/**
 * Pure formatters for execution / turn durations used by the agentic-room
 * "Executions" tab and the side-panel detail.
 *
 * Why pure (not React, not date-fns): the consumer (executions table)
 * re-renders frequently — we'd rather skip the dependency overhead and the
 * date-fns build cost. The agentic-room already standardizes on small
 * pure utils (`thinkingLine.js`, `agentRoomReducer.js`).
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Coerce to non-negative finite number, else NaN. */
function asMs(value) {
  if (value == null) return NaN;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return NaN;
  return n;
}

/**
 * Format a millisecond duration as a short human string.
 *
 *   < 1s   → "850ms"
 *   < 60s  → "12.4s"  (one decimal)
 *   < 1h   → "4m 21s"
 *   < 1d   → "1h 04m"
 *   ≥ 1d   → "2d 03h"
 *
 * Returns ``'—'`` for invalid / unknown durations so the table cell never
 * collapses or shows ``NaN``.
 */
export function formatDuration(ms) {
  const v = asMs(ms);
  if (!Number.isFinite(v)) return '—';
  if (v < SECOND) return `${Math.round(v)}ms`;
  if (v < MINUTE) return `${(v / SECOND).toFixed(1)}s`;
  if (v < HOUR) {
    const m = Math.floor(v / MINUTE);
    const s = Math.round((v - m * MINUTE) / SECOND);
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }
  if (v < DAY) {
    const h = Math.floor(v / HOUR);
    const m = Math.round((v - h * HOUR) / MINUTE);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  const d = Math.floor(v / DAY);
  const h = Math.round((v - d * DAY) / HOUR);
  return `${d}d ${String(h).padStart(2, '0')}h`;
}

/** Coerce ISO/epoch values to epoch ms. Returns NaN on garbage. */
function parseInstant(value) {
  if (value == null) return NaN;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : NaN;
  }
  return NaN;
}

/**
 * Compute a duration between two ISO-or-epoch values. If `endedAt` is
 * absent/falsy the function falls back to ``Date.now()`` so the executions
 * table can show a live ticking duration for in-flight runs.
 *
 * Returns ``'—'`` when ``startedAt`` is missing or invalid.
 */
export function durationBetween(startedAt, endedAt) {
  const s = parseInstant(startedAt);
  if (!Number.isFinite(s)) return '—';
  const e = endedAt ? parseInstant(endedAt) : Date.now();
  if (!Number.isFinite(e) || e < s) return '—';
  return formatDuration(e - s);
}
